import { useState, useCallback, useRef, useEffect } from 'react';
import {
  redeemTechywebTicket,
  requestAuthFromParent,
  setupEmbeddedAuthListener,
} from '@tl4k/frontend-shared';
import { ImageUploader } from './components/ImageUploader';
import { ProcessingStatus } from './components/ProcessingStatus';
import { Model3DPreview } from './components/Model3DPreview';
import {
  generate3DModelComplete,
  uploadImageForTripo,
} from './services/tripoApi';
import {
  loadOpenCV,
  isCVReady,
  processImageForColoring,
  downloadCanvasAsPNG,
} from './services/openCvService';
import { postTl4kGoBack } from './embedding';

type ProcessingState = 'idle' | 'uploading' | 'generating' | 'processing' | 'complete' | 'error';

function App() {
  const [selectedImage, setSelectedImage] = useState<{ file: File; dataUrl: string } | null>(null);
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [coloringPageUrl, setColoringPageUrl] = useState<string | null>(null);
  
  const [processingState, setProcessingState] = useState<ProcessingState>('idle');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const coloringCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;
    const cleanup = setupEmbeddedAuthListener();
    requestAuthFromParent();
    const retry = window.setTimeout(() => requestAuthFromParent(), 300);
    return () => {
      cleanup();
      window.clearTimeout(retry);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authTicket = params.get('auth_ticket');
    if (!authTicket || authTicket.length < 10) return;
    void redeemTechywebTicket(authTicket).finally(() => {
      params.delete('auth_ticket');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
    });
  }, []);

  const handleImageSelected = useCallback(async (file: File, dataUrl: string) => {
    setSelectedImage({ file, dataUrl });
    setModelUrl(null);
    setColoringPageUrl(null);
    setProcessingState('idle');
    setError(null);
    setMessage('');
    setProgress(0);
  }, []);

  const handleProcess = useCallback(async () => {
    if (!selectedImage) return;

    try {
      // Load OpenCV
      if (!isCVReady()) {
        setProcessingState('uploading');
        setMessage('Loading image processing tools...');
        await loadOpenCV();
      }

      // Step 1: Upload image and generate 3D model
      setProcessingState('generating');
      setMessage('Uploading image and generating 3D model...');
      setProgress(10);

      const imageUrl = await uploadImageForTripo(selectedImage.file);
      
      const result = await generate3DModelComplete(imageUrl, (status, prog) => {
        setMessage(`Generating 3D model: ${status}`);
        setProgress(10 + (prog * 0.6)); // 10% to 70%
      });

      setModelUrl(result.modelUrl);
      setProgress(70);

      // Step 2: Process image for coloring page
      setProcessingState('processing');
      setMessage('Creating coloring page...');

      const img = new Image();
      img.src = selectedImage.dataUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const coloringCanvas = await processImageForColoring(img, {
        maxWidth: 2048,
        maxHeight: 2048,
        padding: 50,
      });

      coloringCanvasRef.current = coloringCanvas;
      const coloringDataUrl = coloringCanvas.toDataURL('image/png');
      setColoringPageUrl(coloringDataUrl);

      setProcessingState('complete');
      setMessage('Processing complete!');
      setProgress(100);
    } catch (err) {
      console.error('Processing error:', err);
      setProcessingState('error');
      setError(err instanceof Error ? err.message : 'An error occurred');
      setProgress(0);
    }
  }, [selectedImage]);

  const handleDownloadColoringPage = useCallback(() => {
    if (coloringCanvasRef.current) {
      downloadCanvasAsPNG(coloringCanvasRef.current, 'coloring-page.png');
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedImage(null);
    setModelUrl(null);
    setColoringPageUrl(null);
    setProcessingState('idle');
    setError(null);
    setMessage('');
    setProgress(0);
  }, []);

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <header style={{ marginBottom: '32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => {
              if (postTl4kGoBack()) {
                window.setTimeout(() => window.history.back(), 500);
                return;
              }
              window.history.back();
            }}
            style={{ minHeight: 40, borderRadius: 8, border: '1px solid var(--tl4k-neutral-300)', background: 'var(--tl4k-surface-default)', padding: '0 12px' }}
          >
            ← Indietro
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '8px' }}>
          <img src="/assets/tl4k_logo.svg" alt="TechLAB4Kids Logo" style={{ height: '50px' }} />
          <h1 style={{ fontSize: '32px', margin: 0 }}>
            TL4K AR Editor
          </h1>
        </div>
        <p style={{ fontSize: '16px', color: 'var(--tl4k-neutral-600)', margin: 0 }}>
          Create AR coloring experiences from your images
        </p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left Column - Upload and Processing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Image Upload */}
          <section>
            <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
              1. Upload Image
            </h2>
            {!selectedImage ? (
              <ImageUploader
                onImageSelected={handleImageSelected}
                disabled={processingState !== 'idle' && processingState !== 'complete' && processingState !== 'error'}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                <img
                  src={selectedImage.dataUrl}
                  alt="Selected"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
                <button
                  onClick={handleReset}
                  disabled={processingState !== 'idle' && processingState !== 'complete' && processingState !== 'error'}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: processingState !== 'idle' && processingState !== 'complete' && processingState !== 'error' ? 'not-allowed' : 'pointer',
                    opacity: processingState !== 'idle' && processingState !== 'complete' && processingState !== 'error' ? 0.6 : 1,
                  }}
                >
                  ✕ Remove
                </button>
              </div>
            )}
          </section>

          {/* Process Button */}
          {selectedImage && processingState === 'idle' && (
            <button
              onClick={handleProcess}
              style={{
                padding: '16px 32px',
                fontSize: '18px',
                fontWeight: 'bold',
                backgroundColor: 'var(--tl4k-primary-500)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl4k-primary-600)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl4k-primary-500)')}
            >
              🚀 Generate 3D Model & Coloring Page
            </button>
          )}

          {/* Processing Status */}
          {(processingState !== 'idle') && (
            <section>
              <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
                2. Processing
              </h2>
              <ProcessingStatus
                status={processingState}
                progress={progress}
                message={message}
                error={error}
              />
            </section>
          )}

          {/* Download Button */}
          {processingState === 'complete' && coloringPageUrl && (
            <button
              onClick={handleDownloadColoringPage}
              style={{
                padding: '16px 32px',
                fontSize: '18px',
                fontWeight: 'bold',
                backgroundColor: 'var(--tl4k-secondary-500)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl4k-secondary-600)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl4k-secondary-500)')}
            >
              📥 Download Coloring Page
            </button>
          )}
        </div>

        {/* Right Column - Previews */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* 3D Model Preview */}
          {modelUrl && (
            <section>
              <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
                3D Model Preview
              </h2>
              <Model3DPreview modelUrl={modelUrl} autoRotate={true} />
            </section>
          )}

          {/* Coloring Page Preview */}
          {coloringPageUrl && (
            <section>
              <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
                Coloring Page Preview
              </h2>
              <img
                src={coloringPageUrl}
                alt="Coloring page"
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            </section>
          )}

          {/* Empty State */}
          {!modelUrl && !coloringPageUrl && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                backgroundColor: 'var(--tl4k-surface-warm)',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎨</div>
                <div style={{ fontSize: '18px', color: 'var(--tl4k-neutral-600)' }}>
                  Your 3D model and coloring page will appear here
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <section style={{ marginTop: '48px', padding: '24px', backgroundColor: 'var(--tl4k-surface-warm)', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
          📖 How to Use
        </h2>
        <ol style={{ lineHeight: '1.8', fontSize: '15px' }}>
          <li><strong>Upload an image</strong> - Choose an image of an animal, object, or character</li>
          <li><strong>Generate 3D model</strong> - AI will create a 3D model from your image</li>
          <li><strong>Download coloring page</strong> - Print the edge-detected version</li>
          <li><strong>Color it!</strong> - Let children color the printed page</li>
          <li><strong>AR Viewer</strong> - Use AR Viewer app to see the 3D model with colored texture</li>
        </ol>
      </section>
    </div>
  );
}

export default App;
